import type { MetricRole } from '../../types/config';

export type MetricColumnConfig = {
    key: string;
    title: string;
    editable: boolean;
    allowedDecimals: number;
    minValue: number | null;
    maxValue: number | null;
    preUnit: string;
    postUnit: string;
    groupName: string;
    formula?: string;
    dependencies?: string[];
    order: number;
    defaultVisible: boolean;
    /**
     * Backend visibility role. `fe_hidden` columns are kept for client-side
     * fe_formula math (cascade DAG) but MUST never be rendered / offered in
     * pickers. Absent means displayable (treated as `computed`).
     */
    role?: MetricRole;
};

/**
 * Just the display half of a metric: how to render its numbers. Surfaces that
 * show a metric's value without owning a grid column (e.g. the Optimise Plan
 * constraint table) read this instead of duplicating precision/adornments.
 */
export type MetricFormat = Pick<MetricColumnConfig, 'allowedDecimals' | 'preUnit' | 'postUnit'>;

export const DEFAULT_METRIC_FORMAT: MetricFormat = { allowedDecimals: 1, preUnit: '', postUnit: '' };

/**
 * Fallback for a value whose metric key carries no format in config: a plain
 * grouped integer, no adornments. Preferred over `DEFAULT_METRIC_FORMAT` on
 * report surfaces, where a forced `.0` on every unmapped number reads as a bug.
 */
export const PLAIN_NUMBER_FORMAT: MetricFormat = { allowedDecimals: 0, preUnit: '', postUnit: '' };

/**
 * Shared visibility predicates for the backend `role` field. A missing role is
 * treated as `computed` (displayable). Kept in one place so every display,
 * request, and formula layer applies the exact same rule.
 *
 * - `isDisplayableMetric` — safe to render / offer in pickers, grids, KPI
 *   selectors, and to send to `/report/period-data` & promo-metric endpoints.
 * - `isFormulaOnlyMetric` — `fe_hidden`: exclude from every display surface and
 *   backend request, but STILL include in the client-side fe_formula namespace.
 */
export const isFormulaOnlyMetric = (metric: { role?: MetricRole }): boolean => metric.role === 'fe_hidden';

export const isDisplayableMetric = (metric: { role?: MetricRole }): boolean => !isFormulaOnlyMetric(metric);
